import maplibregl from 'maplibre-gl'
import { screenToLngLat, touchToLngLat as touchToLngLatUtil } from '../geo-utils.js'

/**
 * GestureController — the single authority that owns the map's pointer/touch
 * surface and classifies every interaction into one clean semantic gesture.
 *
 * It replaces the three systems that previously raced each other (the cairn-map
 * native long-press/measure handlers, drag-drawing-manager's separate mousedown/
 * touch listeners, and ad-hoc MapLibre listeners). Everything downstream becomes
 * a subscriber to the semantic callbacks below instead of re-interpreting raw
 * touches.
 *
 * Pan, pinch and double-tap/double-click zoom are deliberately delegated to
 * MapLibre — the controller only recognises the discrete gestures layered on top
 * (tap, long-press / right-click, hold-then-drag / shift-drag box-select) and
 * stays out of MapLibre's way for everything else.
 *
 * ## Touch press-hold ladder (finger down, stationary)
 *
 *   down ──move > MOVE before ARM──► pan (native, controller bows out)
 *        ──released before ARM ─────► tap        (debounced vs double-tap)
 *        ──still down at ARM ───────► armed (haptic tick + onArmed)
 *                 ├─ now drag ──────► box-select (onBoxSelect*)
 *                 └─ released ──────► open detail (second haptic, onTap longPress)
 *
 * Left- and right-click are one surface now (both open the merged popup), so the
 * touch path has a single open: hold past ARM for the "you can box-select"
 * haptic, then lift to open the detail with a second haptic. No separate
 * hold-to-menu tier.
 *
 * ## Desktop (discrete inputs, no timing ladder)
 *
 *   left-click (no drag) ─► tap            right-click ─► action menu
 *   shift+drag ──────────► box-select      ctrl/meta+click ─► tap (modifier set)
 *   left-drag ───────────► pan (native)    hover ─► passthrough
 */

export interface GesturePoint {
  lngLat: maplibregl.LngLat
  point: maplibregl.Point
}

export interface GestureModifiers {
  ctrl: boolean
  shift: boolean
  meta: boolean
  /** A held press (mobile long-press) rather than a quick click — routes empty
   *  ground to "what's here" instead of the discrete-feature-only identify. */
  longPress?: boolean
}

export interface GestureControllerCallbacks {
  /**
   * Single tap / left-click that did not become a pan, long-press or double.
   * Already debounced against double-tap (fires after the double window closes).
   */
  onTap: (g: GesturePoint, mods: GestureModifiers) => void

  /**
   * Long-press (stationary touch hold) or right-click — the location-anchored
   * action menu.
   */
  onActionMenu: (g: GesturePoint) => void

  /** Box / area select lifecycle (touch hold-then-drag, desktop shift+drag). */
  onBoxSelectStart: (g: GesturePoint) => void
  onBoxSelectUpdate: (g: GesturePoint) => void
  onBoxSelectEnd: (g: GesturePoint, mods: GestureModifiers) => void

  /**
   * Policy hook: may a hold-then-drag begin a box-select right now? The host
   * returns false when an active tool wants the drag instead (e.g. a circle /
   * rectangle draw tool), in which case the controller stays out of the drag and
   * lets the host's tool handle it.
   */
  shouldBoxSelect: () => boolean

  /**
   * Master enable. When false the controller ignores everything (e.g. while a
   * modal route-planning flow owns clicks). Optional; defaults to enabled.
   */
  isEnabled?: () => boolean

  /**
   * Whether a selection is active. When true, a quick touch tap toggles a
   * feature immediately (no hold-to-arm needed) — hold-drag still box-selects.
   * Optional.
   */
  selectionActive?: () => boolean

  /**
   * Touch armed (stationary hold crossed the ARM threshold) — host may paint a
   * ring at the point to signal "drag = select, keep holding = menu". Optional.
   */
  onArmed?: (g: GesturePoint) => void
  /** Armed state ended (drag began, menu fired, or released). Optional. */
  onArmedEnd?: () => void

  /**
   * Instant, cheap feedback on a tap candidate before the double-tap window
   * resolves (e.g. highlight the feature under the point). Paired with
   * onTapPreviewCancel when the tap turns out to be the first half of a
   * double-tap. Optional.
   */
  onTapPreview?: (g: GesturePoint) => void
  onTapPreviewCancel?: () => void

  /**
   * Whether a tap should be debounced against the double-tap window before
   * committing. Returns false to land the tap immediately (e.g. placing line/
   * polygon vertices, where the double-tap-zoom protection isn't wanted and the
   * latency would feel laggy). Optional; defaults to debounced.
   */
  shouldDebounceTap?: () => boolean
}

export class GestureController {
  private map: maplibregl.Map | undefined

  // Tunable thresholds (named so they read at the call sites).
  private static readonly MOVE_PX = 10 // tap-vs-pan / drag commitment
  private static readonly ARM_MS = 200 // press → armed-for-select (first haptic)
  private static readonly DBL_MS = 280 // single-vs-double tap window
  private static readonly TOUCH_MOUSE_GUARD_MS = 700 // ignore synthetic mouse after a touch

  // --- touch state machine ---
  private touchState: 'idle' | 'pressing' | 'armed' | 'boxselect' | 'pan' | 'multitouch' = 'idle'
  private touchStartPos: { x: number; y: number } | null = null
  private touchStartLngLat: maplibregl.LngLat | null = null
  private armTimer: ReturnType<typeof setTimeout> | null = null
  // Suppresses the synthetic mouse events a touchscreen fires after a touch.
  private lastTouchTime = 0
  // True while we've disabled MapLibre dragPan for an armed (select/menu) hold.
  private armPanDisabled = false

  // --- single/double-tap debounce (shared by touch + mouse) ---
  private pendingTapTimer: ReturnType<typeof setTimeout> | null = null
  private pendingTapPreviewActive = false

  // --- desktop mouse state ---
  private mouseDownPos: { x: number; y: number } | null = null
  private mouseStartLngLat: maplibregl.LngLat | null = null
  private mouseBoxActive = false
  private mouseDownMods: GestureModifiers = { ctrl: false, shift: false, meta: false }

  // bound handlers (kept for clean removal)
  private boundTouchStart = (e: TouchEvent): void => this.handleTouchStart(e)
  private boundTouchMove = (e: TouchEvent): void => this.handleTouchMove(e)
  private boundTouchEnd = (e: TouchEvent): void => this.handleTouchEnd(e)
  private boundMouseDown = (e: MouseEvent): void => this.handleMouseDown(e)
  private boundMouseMove: ((e: MouseEvent) => void) | null = null
  private boundMouseUp: ((e: MouseEvent) => void) | null = null
  private boundContextMenu = (e: MouseEvent): void => this.handleContextMenu(e)

  constructor(private callbacks: GestureControllerCallbacks) {}

  setMap(map: maplibregl.Map): void {
    this.map = map
  }

  setupEventListeners(): void {
    if (!this.map) {
      return
    }
    const container = this.map.getContainer()

    // Touch: passive:false so we can preventDefault to stop the map pan once a
    // box-select has begun.
    container.addEventListener('touchstart', this.boundTouchStart, { passive: false })
    container.addEventListener('touchmove', this.boundTouchMove, { passive: false })
    container.addEventListener('touchend', this.boundTouchEnd, { passive: false })

    // Mouse: capture-phase mousedown so we can decide before MapLibre starts a
    // box-zoom drag. We own shift+drag (box-select), so disable MapLibre's
    // built-in shift+drag box-zoom to avoid a conflict.
    container.addEventListener('mousedown', this.boundMouseDown, true)
    container.addEventListener('contextmenu', this.boundContextMenu)
    this.map.boxZoom.disable()
  }

  dispose(): void {
    if (this.map) {
      const container = this.map.getContainer()
      container.removeEventListener('touchstart', this.boundTouchStart)
      container.removeEventListener('touchmove', this.boundTouchMove)
      container.removeEventListener('touchend', this.boundTouchEnd)
      container.removeEventListener('mousedown', this.boundMouseDown, true)
      container.removeEventListener('contextmenu', this.boundContextMenu)
    }
    this.detachMouseDragListeners()
    this.clearTouchTimers()
    this.clearPendingTap()
  }

  // ===========================================================================
  // Shared helpers
  // ===========================================================================

  private get enabled(): boolean {
    return this.callbacks.isEnabled ? this.callbacks.isEnabled() : true
  }

  /**
   * Only gestures that begin on the map canvas itself are map gestures. Clicks
   * that originate inside a popup, marker, or control are left alone (the old
   * map.on('click') likewise never fired for non-canvas DOM), so popup content
   * and its close button work normally.
   */
  private startedOnCanvas(target: EventTarget | null): boolean {
    return !!this.map && target === this.map.getCanvas()
  }

  private toGesturePoint(lngLat: maplibregl.LngLat): GesturePoint {
    return { lngLat, point: this.map!.project(lngLat) }
  }

  private modsFrom(e: MouseEvent): GestureModifiers {
    return { ctrl: e.ctrlKey, shift: e.shiftKey, meta: e.metaKey }
  }

  private haptic(style: 'light' | 'medium' | 'heavy'): void {
    try {
      const cap = (window as unknown as Record<string, unknown>).Capacitor as
        | { Plugins?: { Haptics?: { impact: (opts: { style: string }) => void } } }
        | undefined
      cap?.Plugins?.Haptics?.impact({ style })
    } catch {
      // Haptics unavailable on this platform — non-fatal.
    }
  }

  /**
   * A tap candidate: emit onTap, but debounced so the first tap of a double-tap
   * (which zooms via MapLibre) does not flash the single-tap action. We give
   * instant cheap feedback (onTapPreview) immediately and commit the real tap
   * after the double window closes.
   */
  private registerTap(g: GesturePoint, mods: GestureModifiers, immediate = false): void {
    // Land the tap immediately when:
    //  - a context wants it (drawing a line vertex-by-vertex), or
    //  - it's an armed-release: the finger was held past the first haptic, so it
    //    can't be the quick first tap of a double-tap-to-zoom — no debounce lag.
    if (immediate || (this.callbacks.shouldDebounceTap && !this.callbacks.shouldDebounceTap())) {
      this.callbacks.onTap(g, mods)
      return
    }
    if (this.pendingTapTimer) {
      // Second tap inside the window → it's a double-tap. Suppress the pending
      // single tap and let MapLibre's double-tap/double-click zoom proceed.
      this.clearPendingTap()
      return
    }
    if (this.callbacks.onTapPreview) {
      this.callbacks.onTapPreview(g)
      this.pendingTapPreviewActive = true
    }
    this.pendingTapTimer = setTimeout(() => {
      this.pendingTapTimer = null
      this.pendingTapPreviewActive = false
      this.callbacks.onTap(g, mods)
    }, GestureController.DBL_MS)
  }

  private clearPendingTap(): void {
    if (this.pendingTapTimer) {
      clearTimeout(this.pendingTapTimer)
      this.pendingTapTimer = null
    }
    if (this.pendingTapPreviewActive) {
      this.pendingTapPreviewActive = false
      this.callbacks.onTapPreviewCancel?.()
    }
  }

  // ===========================================================================
  // Touch recogniser
  // ===========================================================================

  private clearTouchTimers(): void {
    if (this.armTimer) {
      clearTimeout(this.armTimer)
      this.armTimer = null
    }
  }

  private resetTouch(): void {
    this.clearTouchTimers()
    if (this.touchState === 'armed') {
      this.callbacks.onArmedEnd?.()
    }
    this.restorePan()
    this.touchState = 'idle'
    this.touchStartPos = null
    this.touchStartLngLat = null
  }

  /** Re-enable MapLibre dragPan if we disabled it for an armed hold. */
  private restorePan(): void {
    if (this.armPanDisabled) {
      this.map?.dragPan.enable()
      this.armPanDisabled = false
    }
  }

  private handleTouchStart(e: TouchEvent): void {
    if (!this.map || !this.enabled || !this.startedOnCanvas(e.target)) {
      return
    }
    this.lastTouchTime = performance.now()
    if (e.touches.length > 1) {
      // Pinch / two-finger pan — bow out entirely.
      this.resetTouch()
      this.touchState = 'multitouch'
      return
    }
    const touch = e.touches[0]
    const lngLat = touchToLngLatUtil(this.map, touch)
    this.touchStartPos = { x: touch.clientX, y: touch.clientY }
    this.touchStartLngLat = lngLat
    this.touchState = 'pressing'

    // In an "immediate" context (a draw/measure tool is active) taps land right
    // away and there's no box-select, so skip the haptic ladder.
    if (!this.immediateTapContext()) {
      this.armTimer = setTimeout(() => this.arm(), GestureController.ARM_MS)
    }
  }

  /** Whether the host wants taps to land immediately (drawing/measuring). */
  private immediateTapContext(): boolean {
    return !!this.callbacks.shouldDebounceTap && !this.callbacks.shouldDebounceTap()
  }

  private arm(): void {
    if (this.touchState !== 'pressing' || !this.touchStartLngLat) {
      return
    }
    this.touchState = 'armed'
    // Hold committed: stop MapLibre panning now so a following select-drag (or
    // the menu) never pans the map first. Restored when the gesture resolves.
    if (this.callbacks.shouldBoxSelect()) {
      this.map?.dragPan.disable()
      this.armPanDisabled = true
    }
    this.haptic('light')
    this.callbacks.onArmed?.(this.toGesturePoint(this.touchStartLngLat))
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.map) {
      return
    }
    if (this.touchState === 'boxselect') {
      e.preventDefault()
      const lngLat = touchToLngLatUtil(this.map, e.touches[0])
      this.callbacks.onBoxSelectUpdate(this.toGesturePoint(lngLat))
      return
    }
    if (this.touchState !== 'pressing' && this.touchState !== 'armed') {
      return
    }
    if (e.touches.length !== 1 || !this.touchStartPos) {
      return
    }
    const dx = e.touches[0].clientX - this.touchStartPos.x
    const dy = e.touches[0].clientY - this.touchStartPos.y
    const moved =
      Math.abs(dx) > GestureController.MOVE_PX || Math.abs(dy) > GestureController.MOVE_PX
    if (!moved) {
      return
    }

    if (this.touchState === 'armed' && this.callbacks.shouldBoxSelect() && this.touchStartLngLat) {
      // Hold-then-drag → box-select. Take over the gesture from MapLibre.
      e.preventDefault()
      this.clearTouchTimers()
      this.callbacks.onArmedEnd?.()
      this.touchState = 'boxselect'
      this.callbacks.onBoxSelectStart(this.toGesturePoint(this.touchStartLngLat))
      const lngLat = touchToLngLatUtil(this.map, e.touches[0])
      this.callbacks.onBoxSelectUpdate(this.toGesturePoint(lngLat))
      return
    }

    // Moved before arming (or armed but not box-selecting) → it's a pan. Bow out
    // and let MapLibre handle it.
    this.resetTouch()
    this.touchState = 'pan'
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.map) {
      return
    }
    this.lastTouchTime = performance.now()
    const state = this.touchState

    if (state === 'boxselect') {
      e.preventDefault()
      const touch = e.changedTouches.length > 0 ? e.changedTouches[0] : undefined
      const lngLat = touch ? touchToLngLatUtil(this.map, touch) : this.touchStartLngLat
      if (lngLat) {
        // A touch carries no modifier keys.
        this.callbacks.onBoxSelectEnd(this.toGesturePoint(lngLat), {
          ctrl: false,
          shift: false,
          meta: false,
        })
      }
      this.resetTouch()
      return
    }

    if ((state === 'pressing' || state === 'armed') && this.touchStartLngLat) {
      // Only a held tap counts as a left-click: lifted after the first haptic
      // (armed), with no drag → identify, instantly. A quick tap before arming
      // does nothing here — it stays free for MapLibre's double-tap-to-zoom.
      const g = this.toGesturePoint(this.touchStartLngLat)
      this.clearTouchTimers()
      if (state === 'armed') {
        this.callbacks.onArmedEnd?.()
      }
      this.restorePan()
      this.touchState = 'idle'
      this.touchStartPos = null
      this.touchStartLngLat = null
      // Armed release (held past the first haptic) opens the detail popup, with
      // a second haptic firing as it opens. A quick release only counts in an
      // immediate context (placing a draw vertex / measure point) — and gets no
      // haptic; in explore a quick release does nothing, leaving it free for
      // MapLibre's double-tap-to-zoom.
      if (state === 'armed') {
        this.haptic('medium')
        this.registerTap(g, { ctrl: false, shift: false, meta: false, longPress: true }, true)
      } else if (this.immediateTapContext() || this.callbacks.selectionActive?.()) {
        // Immediate contexts (draw/measure) place a point; while a selection is
        // active, a quick tap toggles a feature without needing to hold first.
        this.registerTap(g, { ctrl: false, shift: false, meta: false }, true)
      }
      return
    }

    // pan / multitouch / idle — clear once all fingers are up.
    if (e.touches.length === 0) {
      this.resetTouch()
    }
  }

  // ===========================================================================
  // Desktop mouse recogniser
  // ===========================================================================

  private handleMouseDown(e: MouseEvent): void {
    if (!this.map || !this.enabled || e.button !== 0 || !this.startedOnCanvas(e.target)) {
      return
    }
    // Ignore the synthetic mouse events a touchscreen fires after a touch — the
    // touch handlers already dealt with that gesture.
    if (performance.now() - this.lastTouchTime < GestureController.TOUCH_MOUSE_GUARD_MS) {
      return
    }
    this.mouseDownPos = { x: e.clientX, y: e.clientY }
    this.mouseStartLngLat = screenToLngLat(this.map, e.clientX, e.clientY)
    this.mouseDownMods = this.modsFrom(e)
    this.mouseBoxActive = false

    if (e.shiftKey && this.callbacks.shouldBoxSelect()) {
      // shift+drag = box-select; prevent MapLibre from starting its own drag.
      e.preventDefault()
      e.stopPropagation()
    }
    this.attachMouseDragListeners()
  }

  private attachMouseDragListeners(): void {
    this.detachMouseDragListeners()
    this.boundMouseMove = (e: MouseEvent): void => this.handleMouseDragMove(e)
    this.boundMouseUp = (e: MouseEvent): void => this.handleMouseUp(e)
    document.addEventListener('mousemove', this.boundMouseMove)
    document.addEventListener('mouseup', this.boundMouseUp)
  }

  private detachMouseDragListeners(): void {
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove)
      this.boundMouseMove = null
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp)
      this.boundMouseUp = null
    }
  }

  private handleMouseDragMove(e: MouseEvent): void {
    if (!this.map || !this.mouseDownPos || !this.mouseStartLngLat) {
      return
    }
    const dx = e.clientX - this.mouseDownPos.x
    const dy = e.clientY - this.mouseDownPos.y
    const moved =
      Math.abs(dx) > GestureController.MOVE_PX || Math.abs(dy) > GestureController.MOVE_PX

    if (this.mouseBoxActive) {
      e.preventDefault()
      const lngLat = screenToLngLat(this.map, e.clientX, e.clientY)
      this.callbacks.onBoxSelectUpdate(this.toGesturePoint(lngLat))
      return
    }

    if (moved && this.mouseDownMods.shift && this.callbacks.shouldBoxSelect()) {
      this.mouseBoxActive = true
      this.callbacks.onBoxSelectStart(this.toGesturePoint(this.mouseStartLngLat))
      const lngLat = screenToLngLat(this.map, e.clientX, e.clientY)
      this.callbacks.onBoxSelectUpdate(this.toGesturePoint(lngLat))
      return
    }

    if (moved) {
      // Plain drag → MapLibre pan; this is no longer a click. Stand down.
      this.mouseDownPos = null
    }
  }

  private handleMouseUp(e: MouseEvent): void {
    this.detachMouseDragListeners()
    if (!this.map) {
      return
    }
    if (this.mouseBoxActive) {
      e.preventDefault()
      const lngLat = screenToLngLat(this.map, e.clientX, e.clientY)
      this.callbacks.onBoxSelectEnd(this.toGesturePoint(lngLat), this.modsFrom(e))
      this.mouseBoxActive = false
      this.mouseDownPos = null
      this.mouseStartLngLat = null
      return
    }
    if (this.mouseDownPos && this.mouseStartLngLat) {
      // Mouse never moved past threshold → a click. (modsFrom(e) so ctrl/meta is
      // captured for toggle-select at the routing layer.)
      const g = this.toGesturePoint(this.mouseStartLngLat)
      this.registerTap(g, this.modsFrom(e))
    }
    this.mouseDownPos = null
    this.mouseStartLngLat = null
  }

  private handleContextMenu(e: MouseEvent): void {
    if (!this.map || !this.enabled || !this.startedOnCanvas(e.target)) {
      return
    }
    // On touch, long-press fires a browser contextmenu too — our timer-based
    // action menu already handles that, so suppress the duplicate.
    if (performance.now() - this.lastTouchTime < GestureController.TOUCH_MOUSE_GUARD_MS) {
      e.preventDefault()
      return
    }
    e.preventDefault()
    this.clearPendingTap()
    const lngLat = screenToLngLat(this.map, e.clientX, e.clientY)
    this.callbacks.onActionMenu(this.toGesturePoint(lngLat))
  }
}
